import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AttendanceStats } from "@/components/checkin/AttendanceStats";
import { typeText } from "@/lib/ticketTypes";
import { Ticket, CheckCircle2, Droplets, Users, AlertCircle, Wine, DollarSign, Banknote, CreditCard } from "lucide-react";

function StatCard({ label, value, sub, Icon, tone = "orange", tid }) {
  const tones = {
    orange: "from-orange-500/20 to-orange-500/5 text-orange-400 border-orange-500/30",
    teal:   "from-teal-500/20 to-teal-500/5 text-teal-300 border-teal-500/30",
    rose:   "from-rose-500/20 to-rose-500/5 text-rose-300 border-rose-500/30",
    gold:   "from-amber-500/20 to-amber-500/5 text-amber-300 border-amber-500/30",
    slate:  "from-slate-500/20 to-slate-500/5 text-slate-300 border-slate-500/30",
  };
  return (
    <div className={`relative rounded-2xl border bg-gradient-to-br ${tones[tone]} p-5`} data-testid={tid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-bold">{label}</div>
        <Icon size={20} className={tones[tone].split(" ")[2]} />
      </div>
      <div className="mt-3 font-display text-4xl font-black text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [att, setAtt] = useState(null);

  const load = () => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/guests/stats").then((r) => setAtt(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  if (!d) return <div className="text-slate-400">Loading dashboard…</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Live Event</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">DASHBOARD</h1>
        </div>
        <Link
          to="/scan"
          className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold px-5 py-3 rounded-xl shadow-lg shadow-orange-500/20"
          data-testid="dashboard-goto-scan"
        >
          Open Scanner →
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Tickets" value={d.total.count} Icon={Ticket} tone="orange" tid="stat-total-tickets" />
        <StatCard label="Regular" value={d.regular.count} sub={`${d.regular.allowed} drink credits`} Icon={Users} tone="teal" tid="stat-regular" />
        <StatCard label="VIP" value={d.vip.count} sub={`${d.vip.allowed} drink credits`} Icon={Users} tone="rose" tid="stat-vip" />
        <StatCard label="Drinks Allocated" value={d.total.allowed} sub={d.sales ? `incl. +${d.sales.purchases_total_qty} purchased` : ""} Icon={Droplets} tone="gold" tid="stat-allocated" />
        <StatCard label="Drinks Redeemed" value={d.total.redeemed} Icon={CheckCircle2} tone="orange" tid="stat-redeemed" />
        <StatCard label="Drinks Remaining" value={d.total.remaining} Icon={Wine} tone="teal" tid="stat-remaining" />
        <StatCard label="Fully Used" value={d.counts.fully_used} sub={`${d.counts.partial} partial · ${d.counts.never_scanned} never`} Icon={AlertCircle} tone="rose" tid="stat-fully" />
        {d.employee && (
          <StatCard label="Employee Tickets" value={d.employee.count}
            sub={`${d.employee.base} base allocated · ${d.employee.redeemed} redeemed · ${d.employee.remaining} remaining`}
            Icon={Users} tone="gold" tid="stat-employee" />
        )}
        {d.sales?.credits && (
          <StatCard label="Drink Credit" value={`$${d.sales.credits.outstanding.toFixed(2)}`}
            sub={`$${d.sales.credits.purchased.toFixed(2)} bought · $${d.sales.credits.redeemed.toFixed(2)} used · ${d.sales.credits.complimentary_redemptions} included drinks served`}
            Icon={DollarSign} tone="gold" tid="stat-credits" />
        )}
      </div>

      {d.sales && (
        <div>
          <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
            <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Sales Snapshot</div>
            <div className="text-[11px] text-slate-400">Beer ${d.prices?.BEER ?? 5} · Cocktail ${d.prices?.COCKTAIL ?? 10}</div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Revenue" value={`$${(d.sales.revenue_total ?? 0).toFixed(2)}`} sub={`${d.sales.purchases_total_qty} drinks sold`} Icon={DollarSign} tone="gold" tid="sales-revenue-total" />
            <StatCard label="Cash Revenue" value={`$${(d.sales.revenue_by_payment?.CASH ?? 0).toFixed(2)}`} sub={`${d.sales.purchases_by_payment.CASH} transactions`} Icon={Banknote} tone="teal" tid="sales-revenue-cash" />
            <StatCard label="Card Revenue" value={`$${(d.sales.revenue_by_payment?.STRIPE ?? 0).toFixed(2)}`} sub={`${d.sales.purchases_by_payment.STRIPE} transactions`} Icon={CreditCard} tone="orange" tid="sales-revenue-stripe" />
            <StatCard label="Beers Sold" value={d.sales.purchases_by_drink.BEER} Icon={Wine} tone="gold" tid="sales-beer" />
            <StatCard label="Cocktails Sold" value={d.sales.purchases_by_drink.COCKTAIL} Icon={Wine} tone="rose" tid="sales-cocktail" />
            <StatCard label="Beers Served" value={d.sales.redeemed_by_drink.BEER} Icon={Droplets} tone="gold" tid="served-beer" />
            <StatCard label="Cocktails Served" value={d.sales.redeemed_by_drink.COCKTAIL} Icon={Droplets} tone="rose" tid="served-cocktail" />
          </div>
        </div>
      )}

      <div data-testid="dashboard-guest-attendance">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
          <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Guest Attendance</div>
          <Link to="/check-in" className="text-orange-400 text-sm hover:underline" data-testid="dashboard-goto-checkin">Open Check-In →</Link>
        </div>
        <AttendanceStats stats={att} prefix="dash-attendance" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-800 bg-[#131D33] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-2xl font-bold text-white tracking-tight">RECENT SCANS</h3>
            <Link to="/history" className="text-orange-400 text-sm hover:underline" data-testid="dashboard-view-history">View all →</Link>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {d.recent_scans.length === 0 && <div className="text-slate-500 text-sm">No scans yet.</div>}
            {d.recent_scans.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-[#0B1120] border border-slate-800">
                <div>
                  <div className="text-white font-semibold">{r.ticket_visible} · <span className={typeText(r.ticket_type)}>{r.ticket_type}</span></div>
                  <div className="text-xs text-slate-400">{r.guest_name || "—"} · by {r.staff_email}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-400">{new Date(r.at).toLocaleTimeString()}</div>
                  <div className="text-emerald-400 text-sm font-bold">−1 drink · {r.new_balance} left</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-[#131D33] p-6">
          <h3 className="font-display text-2xl font-bold text-white mb-4 tracking-tight">TICKETS BREAKDOWN</h3>
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-teal-500/10 border border-teal-500/30">
              <div className="flex items-center justify-between">
                <div className="font-display text-xl text-teal-300 font-bold">REGULAR</div>
                <div className="text-slate-400 text-sm">{d.regular.count} tickets</div>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                <div><div className="text-slate-400 text-xs">Allocated</div><div className="text-white font-bold">{d.regular.allowed}</div></div>
                <div><div className="text-slate-400 text-xs">Redeemed</div><div className="text-emerald-400 font-bold">{d.regular.redeemed}</div></div>
                <div><div className="text-slate-400 text-xs">Remaining</div><div className="text-teal-300 font-bold">{d.regular.remaining}</div></div>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30">
              <div className="flex items-center justify-between">
                <div className="font-display text-xl text-rose-300 font-bold">VIP</div>
                <div className="text-slate-400 text-sm">{d.vip.count} tickets</div>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                <div><div className="text-slate-400 text-xs">Allocated</div><div className="text-white font-bold">{d.vip.allowed}</div></div>
                <div><div className="text-slate-400 text-xs">Redeemed</div><div className="text-emerald-400 font-bold">{d.vip.redeemed}</div></div>
                <div><div className="text-slate-400 text-xs">Remaining</div><div className="text-rose-300 font-bold">{d.vip.remaining}</div></div>
              </div>
            </div>
            {d.employee && (
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="breakdown-employee">
                <div className="flex items-center justify-between">
                  <div className="font-display text-xl text-amber-300 font-bold">EMPLOYEE</div>
                  <div className="text-slate-400 text-sm">{d.employee.count} tickets</div>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                  <div><div className="text-slate-400 text-xs">Allocated</div><div className="text-white font-bold">{d.employee.allowed}</div></div>
                  <div><div className="text-slate-400 text-xs">Redeemed</div><div className="text-emerald-400 font-bold">{d.employee.redeemed}</div></div>
                  <div><div className="text-slate-400 text-xs">Remaining</div><div className="text-amber-300 font-bold">{d.employee.remaining}</div></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
